import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useInventoryStore, useJobSetupStore } from "@presentation/stores";
import {
  useNotifications,
  JobSettingsFormBlock,
  SoftwareSettingsFormBlock,
  useInjection,
  VersionSelectSettingsForm,
  NamingFormBlock,
  FastaFileFormBlock,
  RawFileFormBlock,
  JobPipelineSelectionBlock,
  LibraryFileFormBlock,
  SoftwareSelectionFormBlock,
  QuantificationSelectionBlock,
} from "@presentation/components";
import { useLocation } from "react-router-dom";

import { CreateJob, JobPipelineTypeSchema } from "@domain/schemas/BI";
import { hasPipelineChoice, hasQuantificationChoice } from "@domain/software";
import { CreateJobService } from "@application/useCases";
import { extractProblemDetails, saveDuplicateJobMetadata } from "@presentation/common";
import { PipelineStepEnumSchema } from "@domain/schemas/BI";
import { formatStepName } from "./JobList";
import { isSoftwareLicensed, useLicensedSoftware } from "./components/useLicensedSoftware";
import { getMissingRequirements } from "./jobRequirements";
import { MissingRequirementsSummary } from "./components/RequiredFieldIndicators";
import { useShallow } from "zustand/react/shallow";

const JobSetupForm: React.FC = () => {
  // acquire the items needed from the store
  const {
    CreateJobState,
    redirectToJobList,
    resetCreateJobState,
    rerunAvailableSteps,
    resumeFromStepId,
    sourceSoftwareVersion,
    setResumeFromStepId,
    setAllowCreationButton,
    setShowValidation,
    showValidation,
  } = useJobSetupStore(
    useShallow((state) => ({
      CreateJobState: state.CreateJobState,
      redirectToJobList: state.redirectToJobList,
      handleRedirectToJobListChange: state.handleRedirectToJobListChange,
      resetCreateJobState: state.resetCreateJobState,
      rerunAvailableSteps: state.rerunAvailableSteps,
      resumeFromStepId: state.CreateJobState.resumeFromStepId,
      sourceSoftwareVersion: state.sourceSoftwareVersion,
      setResumeFromStepId: state.setResumeFromStepId,
      setAllowCreationButton: state.setAllowCreationButton,
      setShowValidation: state.setShowValidation,
      showValidation: state.showValidation,
    })),
  );

  // import the resetSelectedGridData from the InventoryStore
  const { resetSelectedGridData } = useInventoryStore(
    useShallow((state) => ({
      resetSelectedGridData: state.resetSelectedGridData,
    })),
  );

  // Dialog state + submitting flag
  const [visible, setVisible] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  const { t: loc } = useTranslation();
  const { pushNotification } = useNotifications();

  // pre-setup the CreateJobService and inject it
  const createJobService = useInjection(CreateJobService);
  const location = useLocation();

  // The cluster is the source of truth for engine licensing, and it is also checked in rerun mode
  // where the engine picker is hidden but the job would still run on an unlicensed engine.
  const licenseState = useLicensedSoftware();
  const selectedSoftwareLicensed = isSoftwareLicensed(licenseState, CreateJobState.software);
  const rerunJobId = React.useMemo(() => new URLSearchParams(location.search).get("rerunJobId"), [location.search]);
  const isRerunMode = !!rerunJobId;
  const stepTypeToStableId: Record<PipelineStepEnumSchema, string> = React.useMemo(
    () => ({
      [PipelineStepEnumSchema.DownloadData]: "11111111-1111-1111-1111-111111111111",
      [PipelineStepEnumSchema.PulsarStep1]: "22222222-2222-2222-2222-222222222222",
      [PipelineStepEnumSchema.PulsarStep2]: "33333333-3333-3333-3333-333333333333",
      [PipelineStepEnumSchema.PulsarStep3]: "44444444-4444-4444-4444-444444444444",
      [PipelineStepEnumSchema.LibraryGeneration]: "55555555-5555-5555-5555-555555555555",
      [PipelineStepEnumSchema.DiaAnalysis]: "66666666-6666-6666-6666-666666666666",
      [PipelineStepEnumSchema.SneCombine]: "88888888-8888-8888-8888-888888888888",
      [PipelineStepEnumSchema.SneMerge]: "77777777-7777-7777-7777-777777777777",
      [PipelineStepEnumSchema.PsarMerging]: "99999999-9999-9999-9999-999999999999",
    }),
    [],
  );
  const rerunStepOptions = React.useMemo(
    () => rerunAvailableSteps.map((step) => ({ id: stepTypeToStableId[step.type as PipelineStepEnumSchema], label: `${formatStepName(step.type, CreateJobState.software)}` })),
    [rerunAvailableSteps, stepTypeToStableId, CreateJobState.software],
  );
  const selectedRerunStep = rerunStepOptions.find((step) => step.id === resumeFromStepId) ?? null;
  // An engine with a single pipeline has nothing to choose, so the selector is hidden.
  const showsPipelineChoice = hasPipelineChoice(CreateJobState.software);
  const showsQuantificationChoice = hasQuantificationChoice(CreateJobState.software);
  const isRerunWithArchiveSelection =
    isRerunMode && (resumeFromStepId === stepTypeToStableId[PipelineStepEnumSchema.PulsarStep1] || resumeFromStepId === stepTypeToStableId[PipelineStepEnumSchema.LibraryGeneration]);
  // Archives live inside the Advanced Settings disclosure (SF-2883), but the pipeline
  // and rerun conditions that decide whether they apply at all are owned here, so the
  // resolved answer is handed to the settings block rather than the rerun state.
  const canSelectArchives =
    (CreateJobState.pipelineType === JobPipelineTypeSchema.DirectDIA || CreateJobState.pipelineType === JobPipelineTypeSchema.LibraryGeneration) && (!isRerunMode || isRerunWithArchiveSelection);
  // A single place decides the copy so the JSX below stays a plain expression.
  const licenseBlockingMessage = React.useMemo(() => {
    if (selectedSoftwareLicensed) return null;
    switch (licenseState.status) {
      case "ready": {
        return `${CreateJobState.software} is not licensed in this deployment. Add a license key on the About page before creating a job.`;
      }
      case "error": {
        return "Could not read the search engine license status. Retrying before a job can be created.";
      }
      case "unknown": {
        return "Waiting for the cluster to report the search engine license before a job can be created.";
      }
      default: {
        return "Checking the search engine license before a job can be created.";
      }
    }
  }, [selectedSoftwareLicensed, licenseState.status, CreateJobState.software]);

  const missingRequirements = React.useMemo(() => getMissingRequirements(CreateJobState), [CreateJobState]);
  const missingSummaryRef = React.useRef<HTMLDivElement>(null);

  const handleSubmit = () => {
    // Validation is deferred to this point on purpose: the form stays neutral
    // until the user actually asks to create the job, and only then reports what
    // is outstanding. The button is left enabled so the reason is reachable —
    // a disabled button can state that something is wrong but never what.
    if (!selectedSoftwareLicensed || missingRequirements.length > 0) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    setVisible(true);
  };

  // The store update above only lands after this render commits, so scrolling/focusing here
  // would still see the pre-render ref. Doing it in an effect guarantees the summary exists.
  useEffect(() => {
    if (!showValidation || missingRequirements.length === 0) return;
    missingSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    missingSummaryRef.current?.focus();
  }, [showValidation, missingRequirements]);

  // function that will be run when user confirms the new job
  const handleDialogSubmit = async () => {
    if (submitting) return; // guard against double clicks
    setSubmitting(true);
    setVisible(false); // ⟵ close the dialog immediately (optimistic close)

    try {
      const createdJob = await createJobService.createJobAsync(CreateJobState as CreateJob);

      if (isRerunMode && rerunJobId && createdJob?.id) {
        saveDuplicateJobMetadata({
          jobId: createdJob.id,
          sourceJobId: rerunJobId,
          sourceJobName: CreateJobState.sourceJobName ?? "Unknown source job",
          sourceAnalysisName: CreateJobState.sourceAnalysisName ?? null,
          sourceCreatedAt: CreateJobState.sourceCreatedAt ?? null,
          sourceSoftwareVersion,
          duplicatedAt: new Date().toISOString(),
          restartFromStepId: CreateJobState.resumeFromStepId ?? null,
          reusedArtifacts: {
            rawFiles: (CreateJobState.rawFiles ?? []).map((file) => file.path),
            fastaFiles: CreateJobState.fastaFiles ?? [],
            libraryFiles: (CreateJobState.libraryFiles ?? []).map((file) => file.path),
            archiveFiles: (CreateJobState.archiveFiles ?? []).map((file) => file.path),
            softwareSettings: CreateJobState.softwareSettingsFile ? [CreateJobState.softwareSettingsFile] : [],
            conditionSetup: CreateJobState.conditionSetupFile ? [CreateJobState.conditionSetupFile] : [],
            modificationRepository: CreateJobState.modificationRepoFile ? [CreateJobState.modificationRepoFile] : [],
            reportSettings: CreateJobState.reportSettingsFiles ?? [],
            goFiles: CreateJobState.goFiles ?? [],
          },
        });
      }

      // Show success notification only if not redirecting (to avoid duplicate notifications)
      if (!redirectToJobList) {
        pushNotification(isRerunMode ? "Job duplicated and scheduled." : loc("newJobCreated"), "success");
      }

      if (redirectToJobList) {
        localStorage.setItem("jobCreationSuccess", "true");
      } else {
        localStorage.removeItem("jobCreationSuccess");
      }

      if (redirectToJobList) {
        globalThis.location.assign("/");
      }
    } catch (error: unknown) {
      const { title, detail, messages, status } = extractProblemDetails(error);
      const header = title || loc("jobCreationFailed");
      const statusPart = status ? ` [${status}]` : "";
      const lines: string[] = [];
      if (detail?.trim()) lines.push(detail.trim());
      if (messages.length > 0) lines.push(...messages.map((m) => m.trim()).filter(Boolean));

      const text = lines.length > 0 ? `${header}${statusPart}\n• ${lines.join("\n• ")}` : `${header}${statusPart}`;

      pushNotification(text, "error", 10_000);
    } finally {
      setSubmitting(false);
    }
  };

  // success toast after redirect (if any)
  useEffect(() => {
    if (localStorage.getItem("jobCreationSuccess")) {
      pushNotification(isRerunMode ? "Job duplicated and scheduled." : loc("newJobCreated"), "success", 10_000);
      localStorage.removeItem("jobCreationSuccess");
    }

    // enable/disable Create Job button
    // The requirement list is the single source of truth for form completeness. The
    // license check stays separate: the engine must have a confirmed license in the
    // cluster, otherwise the job would be admitted here only to fail in the orchestrator.
    setAllowCreationButton(selectedSoftwareLicensed && getMissingRequirements(CreateJobState).length === 0);
  }, [CreateJobState, selectedSoftwareLicensed, setAllowCreationButton, loc, pushNotification, isRerunMode]);

  // Once everything outstanding has been supplied the amber marks have nothing
  // left to point at, so they are cleared rather than left on screen.
  useEffect(() => {
    if (missingRequirements.length === 0) setShowValidation(false);
  }, [missingRequirements, setShowValidation]);

  // define the reset function. We will use this to reset the form
  const resetForm = () => {
    resetCreateJobState();
    // also reset the selectedGridData using resetSelectedGridData function from the InventoryStore
    resetSelectedGridData();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {visible && (
        <Dialog
          className=""
          title={
            isRerunMode
              ? "A new job will be created from this job configuration and scheduled to run. Do you wish to proceed?"
              : "The current job setup will be scheduled to run once you confirm. Do you wish to proceed?"
          }
          onClose={() => setVisible(false)} // ⟵ always closable
        >
          {/* <Checkbox value={redirectToJobList} onChange={handleRedirectToJobListChange} label="Auto-redirect to job list" /> */}
          <DialogActionsBar>
            <Button themeColor="primary" onClick={handleDialogSubmit} disabled={submitting}>
              {submitting ? "Scheduling..." : "Confirm"}
            </Button>
            <Button onClick={() => setVisible(false)} disabled={submitting}>
              Cancel
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      <div className="job-setup-form" style={{ flex: 1, overflowY: "auto", paddingLeft: "8px", paddingRight: "8px" }}>
        <Form
          onSubmit={handleSubmit}
          render={() => (
            <FormElement>
              <NamingFormBlock />

              {!isRerunMode && <SoftwareSelectionFormBlock />}
              <VersionSelectSettingsForm />
              {!isRerunMode && showsPipelineChoice && <JobPipelineSelectionBlock />}
              {showsQuantificationChoice && <QuantificationSelectionBlock />}
              <SoftwareSettingsFormBlock />
              {isRerunMode && rerunStepOptions.length > 0 && (
                <fieldset className={"k-form-fieldset"}>
                  <legend className="k-form-legend">
                    <strong>Restart from:</strong>
                  </legend>
                  <div style={{ marginBottom: "10px" }}>
                    <DropDownList
                      style={{ width: "100%" }}
                      data={rerunStepOptions}
                      textField="label"
                      dataItemKey="id"
                      value={selectedRerunStep}
                      onChange={(event) => setResumeFromStepId(event.value?.id ?? null)}
                    />
                  </div>
                </fieldset>
              )}
              <RawFileFormBlock />
              {CreateJobState.pipelineType !== JobPipelineTypeSchema.LibraryBased && <FastaFileFormBlock />}
              {CreateJobState.pipelineType === JobPipelineTypeSchema.LibraryBased && <LibraryFileFormBlock />}
              <JobSettingsFormBlock canSelectArchives={canSelectArchives} />

              <fieldset className={"k-form-fieldset"}>
                {licenseBlockingMessage && (
                  <div role="alert" style={{ color: "#b45309", marginBottom: 8 }}>
                    {licenseBlockingMessage}
                  </div>
                )}
                <MissingRequirementsSummary ref={missingSummaryRef} missing={showValidation ? missingRequirements : []} />
                <div className="k-form-buttons">
                  <Button type="submit" themeColor={"primary"} disabled={submitting} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={handleSubmit}>
                    {isRerunMode ? "Duplicate And Run" : "Create Job"}
                  </Button>
                  <Button type="reset" themeColor={"secondary"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={resetForm} disabled={submitting}>
                    Reset
                  </Button>
                </div>
                <br />
              </fieldset>
            </FormElement>
          )}
        />
      </div>
    </div>
  );
};

export default JobSetupForm;
